import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useAccount, useDisconnect } from 'wagmi'
import { setUserAuthToken, useAuth } from '../../hooks/useAuth'
import usePrevious from '../../hooks/usePrevious'
import { WalletProvider } from '../../lib/wagmi/connectors'
import AuthStore from '../../stores/AuthStore'
import { log, LogCategories } from '../../utils/logger'
import Button from '../core/Button/Button'
import { Flex } from '../core/Flex'
import { ArpeggiLightLogo } from '../core/icons'
import Spacer from '../core/Spacer/Spacer'
import Text from '../core/Text/Text'
import { Spinner } from '../platform/explore/common/utils'
import { AuthFlowPosition } from './AuthFlowModal'

export const SignMessage = ({
  setFlowPosition
}: {
  setFlowPosition: React.Dispatch<React.SetStateAction<AuthFlowPosition>>
}) => {
  const { getAndSetUser, getLoginSignature, getWalletProviderCookie, logout } = useAuth()
  const { address: walletAddress } = useAccount()
  const { disconnectAsync } = useDisconnect()
  const [isLoading, setIsLoading] = useState(false)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const previousWalletAddress = usePrevious(walletAddress)

  useEffect(() => {
    if (previousWalletAddress !== undefined && previousWalletAddress !== walletAddress) {
      // currently we are just logging out users if they try to switch accounts
      logout()
    }
  }, [previousWalletAddress, walletAddress])

  const onClickVerifyAccount = async () => {
    const { setIsLoggedIn } = AuthStore
    const snackbarMessage =
      getWalletProviderCookie() === WalletProvider.MAGICLINK
        ? 'Your Magic wallet is signing your transaction.'
        : 'Please approve the signature request in your wallet.'
    const snackKey = enqueueSnackbar(snackbarMessage, {
      variant: 'modalInfo',
      persist: true,
      preventDuplicate: true
    })

    try {
      setIsLoading(true)
      if (walletAddress !== undefined) {
        const signWalletAddressRes = await getLoginSignature()

        if (signWalletAddressRes === null) {
          enqueueSnackbar('There was an error signing your wallet, please try again', {
            variant: 'modalError'
          })
          return
        }

        setUserAuthToken(signWalletAddressRes)

        const user = await getAndSetUser()

        log.info(
          {
            category: LogCategories.AUTH,
            data: { user }
          },
          'onClickVerifyAccount'
        )

        if (user !== null && user.twitterHandle !== undefined) {
          // this is only necessary while we have seperate bundles, otherwise can just use navigate
          setIsLoggedIn(true)
          window.location.replace('/home')

          return
        }

        if (user !== null && user.twitterHandle === undefined) {
          setFlowPosition('verifyOnTwitter')
          setIsLoggedIn(true)

          return
        }

        if (user === null) {
          setFlowPosition('createAccount')

          return
        }

        closeSnackbar(snackKey)
      }

      setIsLoading(false)
    } catch (error) {
      // TODO: handle wallet signing issues gracefully with user facing info
      console.error(error)
      enqueueSnackbar('There was an error signing your wallet, please try again', {
        variant: 'modalError'
      })
    } finally {
      closeSnackbar(snackKey)

      setIsLoading(false)
    }
  }
  const onClickDisconnect = async () => {
    setIsLoading(true)
    await disconnectAsync()
    setIsLoading(false)
  }

  return (
    <Flex direction={'column'} css={{ justifyContent: 'center', alignItems: 'center' }}>
      <ArpeggiLightLogo width={'42px'} height={'42px'} />

      <Spacer type={'vertical_5'} />

      <Text css={{ textAlign: 'center' }} size={'32'} type={'primary'}>
        Sign message
      </Text>

      <Spacer type={'vertical_2'} />

      <Text css={{ textAlign: 'center', maxWidth: '350px' }} size={'14'} type={'secondary'}>
        Sign a message in your wallet to verify your account and log in
      </Text>

      <Spacer type={'vertical_5'} />

      <Flex direction={'column'} width="full">
        <Button
          disabled={isLoading}
          type={'dark'}
          big={true}
          rounded={true}
          onClick={onClickVerifyAccount}
        >
          {isLoading ? (
            <Spinner />
          ) : (
            <Text type={'primary_inverted'} size={'14'}>
              Verify Account
            </Text>
          )}
        </Button>

        <Spacer type={'vertical_1'} />

        <Button
          disabled={isLoading}
          type={'white'}
          big={true}
          rounded={true}
          onClick={onClickDisconnect}
        >
          <Text size={'14'}>Disconnect</Text>
        </Button>
      </Flex>
    </Flex>
  )
}
